import { useCallback, useEffect, useRef, type FC } from 'react';
import {
  decrementOpenMenuTooltipSuppression,
  incrementOpenMenuTooltipSuppression,
  registerHoverTooltipOverlayRoot,
  watchMuiOverlayHoverTooltips,
} from '@client/helpers/overlay-hover-tooltips.helpers';

/**
 * Hides all MUI hover tooltips via `document.body.dataset.menuOpen` while
 * `active` is true. Used by menus; theme CSS hides `.MuiTooltip-popper`.
 */
export const useHideHoverTooltips = (active: boolean): void => {
  useEffect(() => {
    if (!active) return;
    incrementOpenMenuTooltipSuppression();
    return decrementOpenMenuTooltipSuppression;
  }, [active]);
};

type OverlayRootRefCallback = (element: Element | null) => void;

/**
 * Registers a custom overlay surface so hover tooltips inside it still work
 * while background tooltips are blocked. Attach the returned callback as `ref`
 * on the overlay root. No-op while `active` is false.
 */
export const useHoverTooltipOverlayRoot = (
  active: boolean,
): OverlayRootRefCallback => {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(
    () => () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    },
    [],
  );

  return useCallback<OverlayRootRefCallback>(
    (element) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!element || !active) return;
      cleanupRef.current = registerHoverTooltipOverlayRoot(element);
    },
    [active],
  );
};

/**
 * App-level watcher so open MUI menus, popovers, and dialogs suppress
 * background hover tooltips without each surface registering itself.
 */
export const OverlayHoverTooltipWatcher: FC = () => {
  useEffect(() => watchMuiOverlayHoverTooltips(), []);
  return null;
};
